import { useEffect, useState } from 'react';
import { Filter, X, Package } from 'lucide-react';
import { useTranslation } from '../../i18n/i18n';
import ReusableTable from '../../components/Tableau';
import { useNavigate } from 'react-router-dom';

import { makeColumns } from './columns';

import api from '../../api/axios';

const ProductPage = ({ lang }) => {
  const { t } = useTranslation(lang);
  const navigate = useNavigate();
  const col = makeColumns(t);
  const [products, setProducts] = useState([])
  const [loading, setLoading] = useState(true);

  const products_url = process.env.REACT_APP_API_PRODUCT_URL

  const [filters, setFilters] = useState({
    serviceType: '',
    status: '',
    supplier: ''
  });

  const fetchAllProducts = async () => {
    try {
      setLoading(true);

      // 1. Charger la page 1
      const firstResponse = await api.get(products_url, {
        params: { page: 1 },
      });

      const firstData = firstResponse.data;
      const lastPage = firstData.last_page;

      let allProducts = firstData.data.map(product => ({
        id: product.id,
        product: product[lang] || product.en || product.fr || product.es || "N/A",
        serviceType: product.service,
        supplier: `Provider ${product.provider_id}`,
        phone: product.phone || "N/A",
        location: product.address,
        status: product.status,
        activeStatus: product.status === "published" ? "actif" : "inactif",
        completion: product.completion_percentage,
      }));

      // 2. Charger les autres pages EN PARALLÈLE
      const promises = [];

      for (let page = 2; page <= lastPage; page++) {
        promises.push(
          api
            .get(products_url, { params: { page } })
            .then(res =>
              res.data.data.map(product => ({
                id: product.id,
                product: product[lang] || product.en || product.fr || product.es || "N/A",
                serviceType: product.service,
                supplier: `Provider ${product.provider_id}`,
                phone: product.phone || "N/A",
                location: product.address,
                status: product.status,
                activeStatus: product.status === "published" ? "actif" : "inactif",
                completion: product.completion_percentage,
              }))
            )
        );
      }

      const pagesResults = await Promise.all(promises);

      pagesResults.forEach(page => {
        allProducts.push(...page);
      });

      setProducts(allProducts);

    } catch (err) {
      console.error("Erreur API produits :", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllProducts();
  }, []);


  const handleFilterChange = (filterName, value) => {
    setFilters(prev => ({
      ...prev,
      [filterName]: value
    }));
  };

  const applyFilters = () => {
    console.log('Filters applied:', filters);
  };

  const clearFilters = () => {
    setFilters({
      serviceType: '',
      status: '',
      supplier: ''
    });
  };

  const handleDetails = (service) => {
    navigate(`/products/${service.id}`);
  };

  const handleRowClick = (service) => {
    handleDetails(service);
  };

  const styles = {
    container: {
      padding: '24px',
      background: '#f9fafb',
      minHeight: '100vh'
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '24px'
    },
    title: {
      fontSize: '28px',
      fontWeight: 'bold',
      color: '#1f2937',
      margin: 0
    },
    titleContainer: {
      display: 'flex',
      alignItems: 'center',
      gap: '16px'
    },
    filtersContainer: {
      background: 'white',
      padding: '20px',
      borderRadius: '12px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      marginBottom: '24px'
    },
    filtersGrid: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: '16px',
      marginBottom: '16px'
    },
    filterGroup: {
      display: 'flex',
      flexDirection: 'column',
      gap: '8px'
    },
    filterLabel: {
      fontSize: '14px',
      fontWeight: '600',
      color: '#374151',
      marginBottom: '4px'
    },
    select: {
      padding: '10px 12px',
      border: '1px',
      borderStyle: 'solid',
      borderColor: '#d1d5db',
      borderRadius: '8px',
      fontSize: '14px',
      background: 'white'
    },
    filterButtons: {
      display: 'flex',
      gap: '12px',
      justifyContent: 'flex-start'
    },
    primaryButton: {
      padding: '10px 20px',
      background: '#00a194ff',
      color: 'white',
      border: 'none',
      borderRadius: '8px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      fontWeight: '500'
    },
    secondaryButton: {
      padding: '10px 20px',
      background: '#6b7280',
      color: 'white',
      border: 'none',
      borderRadius: '8px',
      cursor: 'pointer',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      fontWeight: '500'
    }
  };

  return (
    <div style={styles.container}>
      {/* Header */}
      <div style={styles.header}>
        <div style={styles.titleContainer}>
          <Package size={32} color="#7c3aed" />
          <h1 style={styles.title}>{t('servicesManagement')}</h1>
        </div>
      </div>

      {/* Filtres */}
      <div style={styles.filtersContainer}>
        <div style={styles.filtersGrid}>
          <div style={styles.filterGroup}>
            <label style={styles.filterLabel}>{t('productType')}</label>
            <select
              style={styles.select}
              value={filters.serviceType}
              onChange={(e) => handleFilterChange('serviceType', e.target.value)}
            >
              <option value="">{t('allTypes')}</option>
              <option value="hotel">{t('hotel')}</option>
              <option value="activity">{t('activity')}</option>
              <option value="flight">{t('flight')}</option>
              <option value="transfer">{t('transfer')}</option>
            </select>
          </div>

          <div style={styles.filterGroup}>
            <label style={styles.filterLabel}>{t('status')}</label>
            <select
              style={styles.select}
              value={filters.status}
              onChange={(e) => handleFilterChange('status', e.target.value)}
            >
              <option value="">{t('allStatus')}</option>
              <option value="draft">{t('draft')}</option>
              <option value="pending">{t('pending')}</option>
              <option value="accepted">{t('accepted')}</option>
              <option value="rejected">{t('rejected')}</option>
            </select>
          </div>

          <div style={styles.filterGroup}>
            <label style={styles.filterLabel}>{t('supplier')}</label>
            <select
              style={styles.select}
              value={filters.supplier}
              onChange={(e) => handleFilterChange('supplier', e.target.value)}
            >
              <option value="">{t('allSuppliers')}</option>
              <option value="xvs">xvs</option>
              <option value="extranet">extranet</option>
            </select>
          </div>
        </div>

        <div style={styles.filterButtons}>
          <button style={styles.secondaryButton} onClick={clearFilters}>
            <X size={16} />
            {t('clearFilters')}
          </button>
          <button style={styles.primaryButton} onClick={applyFilters}>
            <Filter size={16} />
            {t('applyFilters')}
          </button>
        </div>
      </div>

      {/* Tableau Reusable */}
      <ReusableTable
        data={products}
        columns={col}
        onDetails={handleDetails}
        onRowClick={handleRowClick}
        lang={lang}
        itemsPerPageOptions={[5, 10, 20, 50]}
        colors={{
          headerBg: '#7c3aed',
          buttonDetails: '#00a194ff'
        }}
      />
    </div>
  );
};

export default ProductPage;